import React, { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/solid";

const FighterScoreCard = ({ selection, idx }) => {
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  const lastUpdate = selection.lastUpdate || {};
  const finalScore = lastUpdate.finalScore || 0;
  const actualOutcome = lastUpdate.outcome || "Pending";
  const baseScore = lastUpdate.baseScore || 0;
  const outcomeBonus = lastUpdate.outcomeBonus || 0;
  const isLotn = selection.lotn || lastUpdate.isLotn || false;

  // Check if event has started (passed via selection or check if any score exists)
  const hasEventStarted =
    selection.hasEventStarted || actualOutcome !== "Pending";

  // Determine if outcome is a win or loss for color coding
  const isWinOutcome = actualOutcome.toLowerCase().includes("win");
  const isLossOutcome = actualOutcome.toLowerCase().includes("loss");

  // Determine score color based on outcome type, not just score value
  const getScoreColor = () => {
    if (isWinOutcome) return "text-green-600";
    if (isLossOutcome) return "text-red-600";
    // For other outcomes (draw, no contest) or when score is non-zero, use score value
    return finalScore >= 0 ? "text-green-600" : "text-red-600";
  };

  // Generate detailed scoring breakdown in plain English
  const generateDetailedBreakdown = () => {
    if (actualOutcome === "Pending") {
      return ["This fight hasn't been scored yet. Check back after the event."];
    }

    const parts = [];

    // Determine if this is a win or loss
    const isWin = finalScore > 0 || (finalScore === 0 && outcomeBonus >= 0);

    if (isWin) {
      // WIN SCENARIO
      if (isLotn) {
        // For LOTN wins, the base score is already doubled
        parts.push(`Base score: ${baseScore.toFixed(2)}`);
        parts.push(
          `Lock of the Night Win: ${baseScore.toFixed(2)} × 2 = ${(baseScore * 2).toFixed(2)}`,
        );

        // Outcome bonus (if any)
        if (outcomeBonus !== 0) {
          parts.push(`${actualOutcome} bonus: +${outcomeBonus.toFixed(2)}`);
          parts.push(
            `Total score: ${(baseScore * 2).toFixed(2)} + ${outcomeBonus.toFixed(2)} = ${finalScore.toFixed(2)}`,
          );
        } else {
          parts.push(`${actualOutcome} bonus: 0.00 (no additional bonus)`);
          parts.push(
            `Total score: ${(baseScore * 2).toFixed(2)} + 0.00 = ${finalScore.toFixed(2)}`,
          );
        }
      } else {
        parts.push(`Base score: ${baseScore.toFixed(2)}`);

        // Outcome bonus (if any)
        if (outcomeBonus !== 0) {
          const sign = outcomeBonus > 0 ? "+" : "";
          parts.push(
            `${actualOutcome} bonus: ${sign}${outcomeBonus.toFixed(2)}`,
          );
          parts.push(
            `Total score: ${baseScore.toFixed(2)} + ${outcomeBonus.toFixed(2)} = ${finalScore.toFixed(2)}`,
          );
        } else {
          parts.push(`${actualOutcome} bonus: 0.00 (no additional bonus)`);
          parts.push(
            `Total score: ${baseScore.toFixed(2)} + 0.00 = ${finalScore.toFixed(2)}`,
          );
        }
      }
    } else {
      // LOSS SCENARIO
      if (isLotn) {
        // For LOTN losses, the base score is multiplied by -2
        parts.push(`Base score: ${baseScore.toFixed(2)}`);
        parts.push(
          `Lock of the Night Loss: ${baseScore.toFixed(2)} × -2 = ${finalScore.toFixed(2)}`,
        );

        // Add outcome penalty if any (e.g., for early round losses)
        if (outcomeBonus !== 0) {
          parts.push(`${actualOutcome} penalty: ${outcomeBonus.toFixed(2)}`);
          parts.push(
            `Total score: ${baseScore.toFixed(2)} + (${outcomeBonus.toFixed(2)}) = ${finalScore.toFixed(2)}`,
          );
        } else {
          parts.push(`${actualOutcome} penalty: 0.00 (no additional penalty)`);
          parts.push(`Total score: ${finalScore.toFixed(2)}`);
        }
      } else {
        // Standard loss - only show the penalty, not base score
        if (outcomeBonus !== 0) {
          parts.push(`${actualOutcome} penalty: ${outcomeBonus.toFixed(2)}`);
          parts.push(`Total score: ${finalScore.toFixed(2)}`);
        } else {
          parts.push(`${actualOutcome} penalty: 0.00 (no additional penalty)`);
          parts.push(`Total score: ${finalScore.toFixed(2)}`);
        }
      }
    }

    return parts;
  };

  const scoringBreakdown = generateDetailedBreakdown();

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-6 h-6 bg-deep-red text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-dark-grey mb-1">
                {selection.fighter}
              </div>
              {isLotn && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gold text-dark-grey mb-1">
                  LOTN
                </span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs text-gray-500 mb-0.5">
              Proj: {(selection.score || 0).toFixed(2)}
            </div>
            <div className="text-xs text-medium-grey">{actualOutcome}</div>
            {(finalScore !== 0 || hasEventStarted) && (
              <div className={`text-sm font-bold ${getScoreColor()}`}>
                {finalScore >= 0 ? "+" : ""}
                {finalScore.toFixed(2)}
              </div>
            )}
          </div>
        </div>

        {/* Scoring Breakdown Dropdown */}
        {actualOutcome !== "Pending" && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => setIsBreakdownOpen(!isBreakdownOpen)}
              className="flex items-center justify-between w-full text-left text-xs text-deep-red hover:text-bright-red font-medium transition-colors"
            >
              <span>{isBreakdownOpen ? "Hide" : "Show"} scoring breakdown</span>
              <ChevronDownIcon
                className={`w-4 h-4 transform transition-transform duration-200 ${
                  isBreakdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isBreakdownOpen && (
              <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
                {scoringBreakdown.map((line, lineIdx) => (
                  <div
                    key={lineIdx}
                    className={`text-xs ${
                      lineIdx === scoringBreakdown.length - 1
                        ? "font-bold text-dark-grey pt-2 mt-2 border-t border-gray-200"
                        : "text-medium-grey"
                    }`}
                  >
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FighterScoreCard;
